/**
 * AQ-Cloud2 top-level layout composition.
 *
 * Owns:
 *   • AppShell composition (Header + Sidebar + Main + StatusBar slots)
 *   • Sidebar collapsed-state persistence (`aq.sidebar.collapsed`)
 *   • Responsive sidebar mode — drawer below 1024px, rail 1024–1439px,
 *     expanded ≥1440px (overridable by the user pin).
 *   • Routing nav items into 3 groups (Workspace / Insights / Admin) using
 *     the optional `NavItemConfig.group` field, with sensible path-based
 *     fallbacks for legacy feature modules.
 *   • Mounting global providers (CommandPalette, StatusBar) and the global
 *     <StatusBar /> renderer.
 */
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Folder,
  LayoutDashboard,
  Menu as MenuIcon,
  // Settings as SettingsIcon,
  X as XIcon,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { AppShell } from '@/components/layout/AppShell'
import { Header } from '@/components/layout/Header'
import { Sidebar, type NavGroup } from '@/components/layout/Sidebar'
import { type NavItemConfig } from '@/components/layout/NavItem'
import { StatusBar } from '@/components/layout/StatusBar'
import { StatusBarProvider } from '@/components/layout/StatusBarProvider'
import { CommandPaletteProvider } from '@/components/layout/CommandPaletteContext'
import { CreateEntityDialogsProvider } from '@/components/layout/CreateEntityDialogsContext'
import { GlobalCreateDialogs } from '@/components/layout/GlobalCreateDialogs'
import { PricingToggleProvider } from '@/platform/pricing/PricingToggleContext'
import { useAuth } from '@/hooks/useAppAuth'
import { useApi } from '@/hooks/useAppApi'
import { usePermissions } from '@/hooks/usePermissions'
import { getFeatureNavItems, getDevFeatureNavItems } from '@/features/registry'
import { buildGroups } from '@/components/layout/navGroups'
import { cn } from '@/lib/utils'

const COLLAPSE_KEY = 'aq.sidebar.collapsed'

/* ── Core nav items always present ─────────────────────────────────── */
const coreNavItems: NavItemConfig[] = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, group: 'workspace' },
  { path: '/files',     label: 'Files',     icon: Folder,           group: 'insights' },
  // { path: '/settings',  label: 'Settings',  icon: SettingsIcon,     group: 'admin' },
]

/* ── Responsive sidebar mode detection ─────────────────────────────── */
type ViewportMode = 'mobile' | 'rail' | 'expanded'

function getMode(): ViewportMode {
  if (typeof window === 'undefined') return 'expanded'
  const w = window.innerWidth
  if (w < 1024) return 'mobile'
  if (w < 1440) return 'rail'
  return 'expanded'
}

function useViewportMode(): ViewportMode {
  const [mode, setMode] = useState<ViewportMode>(() => getMode())
  useEffect(() => {
    const onResize = (): void => setMode(getMode())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  return mode
}

/* ── User mini-card ────────────────────────────────────────────────── */
const UserMiniCard = (): JSX.Element => {
  const { user } = useApi()
  if (!user) return <></>
  const initials = user.name
    ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U'

  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <Avatar className="h-9 w-9 bg-brand text-brand-foreground">
        <AvatarFallback className="bg-brand text-brand-foreground text-[12px] font-semibold">
          {initials}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <div className="text-[13px] font-semibold truncate">{user.name || 'User'}</div>
        <div className="text-[11px] text-muted-foreground truncate">{user.email}</div>
      </div>
    </div>
  )
}

const LoadingScreen = (): JSX.Element => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center space-y-3">
      <div className="text-[20px] font-semibold tracking-tight">
        <span className="text-brand-secondary">Auto</span>
        <span className="text-brand">Quotes</span>
      </div>
      <div className="text-sm text-muted-foreground">Loading…</div>
    </div>
  </div>
)

interface AppLayoutProps {
  children: React.ReactNode
}

const InnerAppLayout = ({ children }: AppLayoutProps): JSX.Element => {
  const { isLoading, isAuthenticated } = useAuth()
  const { permissions } = usePermissions()
  const mode = useViewportMode()
  const location = useLocation()

  /* Inside /admin the AdminLayout owns its own top-bar navigation, so we
     suppress the global app sidebar entirely to avoid the "two sidebars"
     problem. The mobile menu toggle is also suppressed since the admin
     console is a self-contained surface. */
  const isAdminRoute = location.pathname === '/admin' || location.pathname.startsWith('/admin/')

  /* User pin overrides the viewport default — but only between rail and
     expanded. Mobile mode always uses the drawer. */
  const [userPinnedCollapsed, setUserPinnedCollapsed] = useState<boolean | null>(() => {
    if (typeof window === 'undefined') return null
    const raw = window.localStorage.getItem(COLLAPSE_KEY)
    return raw === null ? null : raw === '1'
  })

  useEffect(() => {
    if (userPinnedCollapsed === null) return
    window.localStorage.setItem(COLLAPSE_KEY, userPinnedCollapsed ? '1' : '0')
  }, [userPinnedCollapsed])

  const collapsed =
    mode === 'mobile'
      ? false
      : userPinnedCollapsed ?? (mode === 'rail')

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
  useEffect(() => {
    if (mode !== 'mobile') setMobileDrawerOpen(false)
  }, [mode])

  /* Build groups from core + feature nav items. */
  const groups = useMemo<NavGroup[]>(() => {
    if (!isAuthenticated) return []
    const merged = [...coreNavItems, ...getFeatureNavItems(), ...getDevFeatureNavItems()]
    return buildGroups(merged, permissions)
  }, [isAuthenticated, permissions])

  if (isLoading) return <LoadingScreen />

  const sidebar =
    isAuthenticated && mode !== 'mobile' && !isAdminRoute ? (
      <Sidebar
        groups={groups}
        collapsed={collapsed}
        onToggleCollapsed={() => setUserPinnedCollapsed((v) => !(v ?? mode === 'rail'))}
        header={<UserMiniCard />}
      />
    ) : undefined

  const mobileToggle =
    isAuthenticated && mode === 'mobile' && !isAdminRoute ? (
      <button
        type="button"
        onClick={() => setMobileDrawerOpen(true)}
        aria-label="Open navigation"
        className="w-10 h-10 rounded inline-flex items-center justify-center text-foreground hover:bg-muted"
      >
        <MenuIcon className="h-5 w-5" aria-hidden="true" />
      </button>
    ) : undefined

  return (
    <>
      <AppShell
        header={<Header leadingMobile={mobileToggle} />}
        sidebar={sidebar}
        statusBar={isAuthenticated ? <StatusBar /> : undefined}
      >
        {children}
      </AppShell>

      {isAuthenticated && <GlobalCreateDialogs />}

      {/* Mobile drawer */}
      {mode === 'mobile' && !isAdminRoute && (
        <div
          className={cn(
            'fixed inset-0 z-50 transition-opacity duration-fast',
            mobileDrawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
          )}
          aria-hidden={!mobileDrawerOpen}
        >
          <div
            className="absolute inset-0 bg-foreground/40"
            onClick={() => setMobileDrawerOpen(false)}
          />
          <div
            className={cn(
              'absolute left-0 top-0 bottom-0 bg-card shadow-lg w-[280px] flex flex-col transition-transform duration-normal',
              mobileDrawerOpen ? 'translate-x-0' : '-translate-x-full',
            )}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
          >
            <div className="h-12 px-3 border-b border-border flex items-center justify-between">
              <div className="text-sm font-semibold">Menu</div>
              <button
                type="button"
                onClick={() => setMobileDrawerOpen(false)}
                aria-label="Close menu"
                className="w-8 h-8 rounded inline-flex items-center justify-center hover:bg-muted"
              >
                <XIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <Sidebar
              groups={groups}
              header={
                <Card>
                  <CardContent className="p-3">
                    <UserMiniCard />
                    <div className="mt-2 flex flex-wrap gap-1">
                      {/* render user scopes as small badges in mobile */}
                      <UserScopes />
                    </div>
                  </CardContent>
                </Card>
              }
              className="border-r-0 flex-1"
            />
          </div>
        </div>
      )}
    </>
  )
}

const UserScopes = (): JSX.Element => {
  const { user } = useApi()
  if (!user || !user.scopes?.length) return <></>
  return (
    <>
      {user.scopes.slice(0, 4).map((scope) => (
        <Badge key={scope} variant="secondary" className="text-[10px]">
          {scope}
        </Badge>
      ))}
    </>
  )
}

export const AppLayout = ({ children }: AppLayoutProps): JSX.Element => (
  <CommandPaletteProvider>
    <CreateEntityDialogsProvider>
      <StatusBarProvider>
        <PricingToggleProvider>
          <InnerAppLayout>{children}</InnerAppLayout>
        </PricingToggleProvider>
      </StatusBarProvider>
    </CreateEntityDialogsProvider>
  </CommandPaletteProvider>
)
